import { createUserManager } from '@sdb-develop/learning-redux-oidc-es6';
import environments from '../../app/environments.js';
import { GLOBAL_ID } from '../env/env.js';

const TENANT_KEY = 'tenant';
const TOKEN_PORTAL_KEY = 'tokenPortal';

// The portal of the launch (the LTI consumer's). The standalone player has no portal host or
// Referer, so the Learning API resolves the portal of its requests from the TcgPortalIdentifier
// claim, which the STS sets from the acr_values of each authorize request, silent renews included.
let portalIdentifier = null;

const host = `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`;

// Kept around so clearing the tenant can put the TCG settings back.
const tcgSettings = {
    client_id: 'tcg-composition',
    redirect_uri: `${host}/callback`,
    response_type: 'token id_token',
    // api_tcg_claims puts TcgPortalIdentifier in the access token
    scope: 'openid name api api_tcg_claims idString',
    authority: 'https://sts.competence.biz/identity',
    silent_redirect_uri: `${host}/silentrenew`,
    post_logout_redirect_uri: `${host}/signedout`,
    automaticSilentRenew: true,
    // Defaults to true, but nothing consumes its events and it polls the STS check
    // session iframe every 2 seconds. Worse, it reports a sign out whenever its own
    // iframe request fails - which is what a blocked third-party cookie looks like -
    // so leaving it on is an endless sign-in loop waiting to happen.
    monitorSession: false,
    filterProtocolClaims: false,
    loadUserInfo: false,
};

export const userManager = createUserManager({ ...tcgSettings });

export const getTenant = () => localStorage.getItem(TENANT_KEY);

// env.settings is not available everywhere the authority has to be settled, so fall
// back to the same environment the env slice would have resolved.
const resolveEnvironment = () => {
    const env = window.ENVIRONMENT;

    return environments[['development', 'production'].indexOf(env) === -1 ? 'development' : env];
};

const applySdbIdentitySettings = (tenant, settings) => {
    const { sdbIdentityUrl } = settings ?? resolveEnvironment() ?? {};

    if (!sdbIdentityUrl) {
        return;
    }

    // for SDB Identity if tenant exists then overrule UserManager with SDB Identity
    // settings else fallback to TCG
    userManager._settings._authority = `https://${tenant}.${sdbIdentityUrl}`;
    userManager._settings._client_id = 'sdblearning-composition';
    userManager._settings._response_type = 'code';
    userManager._settings._scope = 'openid profile sdb';
    userManager._settings._redirect_uri = `${host}/callback/`;
    userManager._settings._silent_redirect_uri = `${host}/silentrenew/`;
    userManager._settings._post_logout_redirect_uri = `${host}/signedout/`;
    userManager._settings._acr_values = undefined;
};

const applyTcgSettings = () => {
    userManager._settings._authority = tcgSettings.authority;
    userManager._settings._client_id = tcgSettings.client_id;
    userManager._settings._response_type = tcgSettings.response_type;
    userManager._settings._scope = tcgSettings.scope;
    userManager._settings._redirect_uri = tcgSettings.redirect_uri;
    userManager._settings._silent_redirect_uri = tcgSettings.silent_redirect_uri;
    userManager._settings._post_logout_redirect_uri = tcgSettings.post_logout_redirect_uri;
    userManager._settings._acr_values = portalIdentifier ? `portalIdentifier:${portalIdentifier}` : undefined;
};

// The automatic silent renew runs off oidc-client's own timers, so the authority has
// to be settled as soon as the tenant is known instead of lazily per thunk, which
// left the renew aimed at the TCG authority while holding an SDB Identity token.
export const ensureUserManagerSettings = async (settings) => {
    const tenant = getTenant();

    if (tenant) {
        applySdbIdentitySettings(tenant, settings);
    } else {
        applyTcgSettings();
    }
};

export const setTenant = (tenant) => {
    localStorage.setItem(TENANT_KEY, tenant);
    applySdbIdentitySettings(tenant);
}

export const clearTenant = () => {
    localStorage.removeItem(TENANT_KEY);
    applyTcgSettings();
}

// Call before clearTenant, which applies it to the TCG settings.
export const setPortal = (portal) => {
    portalIdentifier = portal && portal !== GLOBAL_ID ? portal : null;
}

export const getPortal = () => portalIdentifier;

// A stored token from an earlier launch may carry another portal, or none.
export const hasTokenForPortal = () => !portalIdentifier || localStorage.getItem(TOKEN_PORTAL_KEY) === portalIdentifier;

// After a completed sign in, with the portal its request was made for.
export const rememberTokenPortal = (portal) => {
    localStorage.setItem(TOKEN_PORTAL_KEY, portal ?? '');
}

// A tenant left in localStorage by an earlier visit has to be applied before
// loadUser() hands a stored user to the userManager and the renew timers arm.
ensureUserManagerSettings();
